/* @flow */
import { OrderedMap } from 'immutable';
import { datadogLogs } from '@datadog/browser-logs';
import { debounce, isEmpty, flow } from 'lodash';
import React from 'react';
import { DropTarget, DropTargetMonitor } from 'react-dnd';
import Modal from 'react-modal';
import { NativeTypes } from 'react-dnd-html5-backend';
import { connect } from 'react-redux';
import type { InjectedRouter, PlainRoute } from 'react-router';
import { bindActionCreators } from 'redux';
import uuid from 'uuid/v4';
import { isVIP } from 'lib/telemetry';

import { touch } from 'actions/touchActions';
import { withRouter } from 'app/context/Router';
import CurrentUser from 'auth/CurrentUser';
import { Message, TARGET_TYPES } from 'entities/Message';
import type { Target } from 'entities/Message';
import { CHARACTER_LIMIT } from 'chat/components/MessageComposer';
import { TOUCH_KEY as STREAM_SECTION_TOUCH_KEY } from 'chat/components/StreamSectionList';
import linkPreviewQuery from 'chat/queries/linkPreview.gql';
import type { LinkPreview_linkPreview } from 'chat/queries/__generated__/LinkPreview';
import ChatWelcomeMessage from 'chat/components/ChatWelcomeMessage';
import ConfirmDialog from 'components/ConfirmDialog';
import DropZoneModal from 'components/DropZoneModal';
import LayerTrigger from 'components/LayerTrigger';
import chainOnEnter from 'components/lib/chainOnEnter';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import TextWithGlyph from 'components/TextWithGlyph';
import Tooltip from 'components/Tooltip';
import VisualComposer from 'chat/components/FullPageComposer/VisualComposer';

import getRedirectUrlAfterMessageSendToTarget from 'lib/getRedirectUrlAfterMessageSendToTarget';
import EventTracking from 'lib/EventTracking';
import TextEntities from 'lib/TextEntities';
import RemindAPI from 'lib/RemindAPI';
import RequireDesktop from 'lib/RequireDesktop';
import RequireSetup from 'lib/RequireSetup';
import RequireUser from 'lib/RequireUser';
import { flush, saveMessage, saveDidSearchRecipients } from 'reducers/roleBasedMessagingSearch';
import { showToast } from 'reducers/toasts';
import type { ContentSourcesContent_me_composer_contentSourcesContent_items_downloadPreview as DownloadPreview } from 'user/queries/__generated__/ContentSourcesContent';
import { uploadFileRequest, fetchJSONFileByIdRequest } from 'clients/api/fileUploads';
import type { V2File } from 'clients/api/fileUploads';
import ComposerFilePreview from 'chat/components/ComposerFilePreview';
import { uploadFile as uploadFileHelper } from 'lib/fileUploadHelper';

import { ComposeMessageRecipientsInput } from '../SplitComposer';
import SplitComposerHeader from '../SplitComposer/components/SplitComposerHeader';
import ComposerDataContainer from '../SplitComposer/components/ComposerDataContainer';
import type { UIEventContext } from '../SplitComposer/components/ComposerContext';
import 'chat/components/FullPageComposer/styles/FullPageComposerView.scss';

type Props = {
  route: PlainRoute,
  router: InjectedRouter,

  // Redux state
  targets: Target[],
  districtIsSelected: boolean,
  district: ?ComposerOrganization,
  savedMessage: string,
  // "envelope" properties
  didSearchRecipients: boolean,
  draftAnnouncement: ?Message,
  initialTargets?: Target[],
  onMessageSent?: Function,
  messageType: 'message' | 'urgent',

  // Redux dispatchers
  onFlush: () => *,
  saveMessage: (message: string) => *,
  showToast: typeof showToast,
  saveDidSearchRecipients: (x: boolean) => void,

  // Drag 'n drop props
  connectDropTarget: Function,
  isDraggingFile?: boolean,

  // sharesdk url from url params
  shareSdkBodyUrl?: string,
};

type State = {
  text: string,
  scheduledTime: ?Date,
  uuidForNewMessage: ?string,
  linkPreviews: OrderedMap<string, *>,
  discardMessageDialogIsOpen: boolean,
  userHasUnsavedMessage: boolean,
  messageSentOrScheduled: boolean,
  nextLocation?: Location,
  file?: V2File,
  fileData?: File,
  thumbnail?: string,
  uploadProgress?: number,
  uploadError?: Error,
};

export const URGENT_STRING = 'URGENT: ';

const isUrgent = (messageType): boolean => messageType === 'urgent';

const getUiEventContext = (messageType): UIEventContext => ({
  message_type: messageType,
  is_urgent: isUrgent(messageType),
});

export class FullPageComposerView extends React.Component<Props, State> {
  static onEnter = chainOnEnter([RequireUser, RequireSetup, RequireDesktop]);

  state: State;

  constructor(props: Props) {
    super(props);
    this.state = {
      text: '',
      scheduledTime: (null: ?Date),
      uuidForNewMessage: uuid(),
      linkPreviews: new OrderedMap(),
      discardMessageDialogIsOpen: false,
      userHasUnsavedMessage: false,
      messageSentOrScheduled: false,
    };

    const { draftAnnouncement } = props;
    if (draftAnnouncement) {
      Object.assign(this.state, {
        scheduledTime: draftAnnouncement.getSendAt(),
      });

      const body = draftAnnouncement.getBody();
      if (body) {
        Object.assign(this.state, {
          text: body,
        });
      }
      // override the random uuid with one of the initial message, if it has one
      if (draftAnnouncement.getUuid()) {
        Object.assign(this.state, { uuidForNewMessage: draftAnnouncement.getUuid() });
      }

      const linkPreviews = {};
      draftAnnouncement.getLinkPreviews().forEach(p => {
        linkPreviews[p.getUrl()] = {
          title: p.getTitle(),
          description: p.getDescription(),
          url: p.getUrl(),
          image: p.getImageUrl(),
          image_width: p.getImageWidth(),
          image_height: p.getImageHeight(),
          rich_content_type: p.getRichContentType(),
          rich_content_url: p.getRichContentUrl(),
        };
      });
      Object.assign(this.state, { linkPreviews: OrderedMap(linkPreviews) });
    }

    if (props.shareSdkBodyUrl) {
      this.getLinkPreviews([props.shareSdkBodyUrl])
        .then(() => (props.shareSdkBodyUrl = undefined))
        .catch(() => {
          /* do nothing */
        });
    }
  }

  async componentWillMount() {
    const { draftAnnouncement, initialTargets, savedMessage, messageType } = this.props;
    const isUrgentMessage = isUrgent(messageType);

    if (!draftAnnouncement && initialTargets && !savedMessage.length) {
      // urgent messages start with 'Urgent: ' - see URGENT_STRING
      this.setState({ text: isUrgentMessage ? `${URGENT_STRING}` : '' });
    } else if (savedMessage.length) {
      this.setState({ text: savedMessage });
    } else if (isUrgentMessage && !draftAnnouncement) {
      this.setState({ text: URGENT_STRING });
    }
  }

  async componentDidMount() {
    const { text } = this.state;
    const { draftAnnouncement, didSearchRecipients } = this.props;

    if (draftAnnouncement) {
      const fileId = draftAnnouncement.getAttachmentId() || draftAnnouncement.getFile()?.id;
      if (fileId) {
        const uploadedFile = await fetchJSONFileByIdRequest(fileId);
        this.setState({ file: uploadedFile, uploadProgress: 100 });
      }
    }

    if (text) {
      this.props.saveMessage(text);
    }

    // Track that a user proceeded with composing a message after searching for recipients,
    // then reset |didSearchRecipients| for any subsequent searches
    if (didSearchRecipients) {
      datadogLogs.logger.info('search.sla.2020.search.composer.ctr.success.web', {
        is_vip: isVIP(),
      });

      this.props.saveDidSearchRecipients(false);
    }

    EventTracking.trackKey('announcements.composer.view', {
      composer_version: 'web_full_page',
    });

    // https://github.com/ReactTraining/react-router/blob/v3/docs/API.md#setrouteleavehookroute-hook
    // this registers `handleAttemptedRouteChange` to fire anytime the user tries to leave the current route
    this.props.router.setRouteLeaveHook(this.props.route, this.handleAttemptedRouteChange);
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (!isUrgent(prevProps.messageType) && isUrgent(this.props.messageType)) {
      this.appendUrgentPrefix();
    }

    if (prevState.text !== this.state.text) {
      this.props.saveMessage(this.state.text);
    }
  }

  appendUrgentPrefix() {
    this.setState({ text: `${URGENT_STRING}${this.state.text}` });
  }

  handleAttemptedRouteChange = (nextLocation?: Location) => {
    const hasMessageContent =
      this.props.targets.length > 0 || !!this.state.file || this.state.text.trim().length > 0;

    // We don't call |clearReduxState| right away to clear the message content on
    // message send or schedule, so check |messageSentOrScheduled| first.
    if (
      !this.state.messageSentOrScheduled &&
      hasMessageContent &&
      nextLocation &&
      !this.state.discardMessageDialogIsOpen
    ) {
      this.setState({ nextLocation }, this.showUnsavedMessageDialog);
      return false;
    }

    return true;
  };

  forwardUserToNextLocation = () => {
    const { nextLocation, messageSentOrScheduled } = this.state;
    const { didSearchRecipients } = this.props;

    this.clearReduxState();

    // Track when user does not proceed with composing a message after searching
    if (!messageSentOrScheduled && didSearchRecipients) {
      datadogLogs.logger.info('search.sla.2020.search.composer.ctr.rejection.web', {
        is_vip: isVIP(),
      });
    }

    // We should only be calling this method from the confirmation dialog,
    // so we should always have a |nextLocation| - but we don't initialize
    // this component with one so we need to perform this check.
    return nextLocation
      ? this.props.router.push(nextLocation)
      : console.error('FullPageComposerView - tried to route user to nonexistent nextLocation');
  };

  showUnsavedMessageDialog = () => {
    this.setState({ discardMessageDialogIsOpen: true });
  };

  hideUnsavedMessageDialog = () => {
    this.setState({ discardMessageDialogIsOpen: false });
  };

  clearReduxState = () => {
    this.resetFileState();
    this.props.onFlush();
  };

  debouncedGetLinkPreviews = debounce((urls: Array<string>) => this.getLinkPreviews(urls), 500);

  getLinkPreviews = (urls: string[]): Promise<void[]> =>
    Promise.all(urls.map((u: string): Promise<void> => this.getSingleLinkPreview(u, null, null)));

  getSingleLinkPreview = async (
    url: string,
    existingPreview: ?$Shape<LinkPreview_linkPreview>,
    downloadPreview: ?DownloadPreview
  ): Promise<void> => {
    try {
      const data = await RemindAPI.execute({
        query: linkPreviewQuery,
        variables: { ...(existingPreview || {}), url },
      });

      const { linkPreview } = data;
      if (linkPreview) {
        // this is to resolve a bug involving the link preview service returning the _entire_ pdf
        // will also resolve this on the service as well.
        const description = linkPreview.description && linkPreview.description.slice(0, 1024);

        const {
          title,
          imageUrl,
          imageWidth,
          imageHeight,
          richContentType,
          richContentUrl,
        } = linkPreview;

        const { text, linkPreviews } = this.state;
        const linksInText = TextEntities.parseUrls(text).map(entity =>
          text.substring(entity.offset, entity.offset + entity.length)
        );

        let newLinkPreviews = linkPreviews;
        if (linksInText.includes(url)) {
          newLinkPreviews = newLinkPreviews.set(url, {
            title,
            description,
            url,
            image: imageUrl,
            image_width: imageWidth ? String(imageWidth) : imageWidth,
            image_height: imageHeight ? String(imageHeight) : imageHeight,
            rich_content_type: richContentType,
            rich_content_url: richContentUrl,
            downloadPreview,
          });
        }

        this.setState({
          linkPreviews: newLinkPreviews,
        });
      }
    } catch (error) {
      console.error('could not get link preview', error);
    }
  };

  changeText = (text: string) => {
    const { linkPreviews } = this.state;

    // Get previews for links added to text
    const linksInText = TextEntities.parseUrls(text).map(entity =>
      text.substring(entity.offset, entity.offset + entity.length)
    );
    const newLinks = linksInText.filter(url => !linkPreviews.has(url));

    this.debouncedGetLinkPreviews(newLinks);

    // Remove previews for links that are removed from text
    const currentLinkedPreviews = linkPreviews.filter(content => linksInText.includes(content.url));

    this.props.saveMessage(text);

    this.setState({
      text,
      linkPreviews: currentLinkedPreviews,
    });
  };

  changeScheduledTime = (scheduledTime: ?Date) => {
    this.setState({ scheduledTime });
  };

  handleComposerSent = () => {
    this.setState({ messageSentOrScheduled: true });
    const { targets, onMessageSent } = this.props;
    const { scheduledTime } = this.state;

    if (scheduledTime) {
      this.props.showToast('Announcement scheduled', 'success');
    } else {
      this.props.showToast('Message sent', 'success');
    }

    if (onMessageSent) {
      onMessageSent({ scheduledTime, targets });
    }

    if (targets.length > 0) {
      const redirectUrl = getRedirectUrlAfterMessageSendToTarget(targets[0]);

      if (redirectUrl) {
        this.props.router.push(redirectUrl);
      }
    }

    this.clearReduxState();
    touch(STREAM_SECTION_TOUCH_KEY);
  };

  renderDropzoneModal = (): ?React$Element<*> => {
    const { isDraggingFile } = this.props;
    const { fileData } = this.state;
    if (!isDraggingFile) {
      return null;
    }

    return (
      <div>
        <DropZoneModal titleDrop="Drop a file here" isFileUploaded={!!fileData} isHovering />
      </div>
    );
  };

  addLinkedContent = (content: *) => {
    // This method adds content from the content browser.
    const { text, linkPreviews } = this.state;
    const { title, description, contentUrl, imageUrl, downloadPreview } = content;

    if (linkPreviews.has(contentUrl)) {
      return;
    }

    // Only add a space in front of the link if there's already text there
    const hasText = text.trim().length > 0;
    const conditionalSpace = hasText ? ' ' : '';
    this.setState(
      {
        text: `${text}${conditionalSpace}${contentUrl} `,
        // Set an empty object to stop the same content from being added
        // multiple times while the preview resolves
        linkPreviews: linkPreviews.set(contentUrl, {}),
      },
      () => {
        this.getSingleLinkPreview(
          contentUrl,
          {
            url: contentUrl,
            title,
            description,
            imageUrl,
          },
          downloadPreview
        );
      }
    );
  };

  removeLinkPreview = (preview: *) => {
    const { text, linkPreviews } = this.state;
    const { url } = preview;

    this.setState({
      // Try to remove the url with the default whitespace first
      text: text
        .replace(url + ' ', '')
        .replace(url, '')
        .trim(),
      linkPreviews: linkPreviews.remove(url),
    });
  };

  targetsContainGroupsOrOrgs = () => {
    const { targets } = this.props;
    if (targets) {
      for (const target of targets) {
        if (
          target.type === TARGET_TYPES.GROUP_TARGET ||
          target.type === TARGET_TYPES.ORGANIZATION_TARGET
        ) {
          return true;
        }
      }
    }
    return false;
  };

  characterLimit = () => {
    return this.targetsContainGroupsOrOrgs()
      ? CurrentUser.getUser().getAnnouncementCharactersLimit()
      : CHARACTER_LIMIT;
  };

  characterLimitWithFiles = () => {
    return this.targetsContainGroupsOrOrgs()
      ? CurrentUser.getUser().getAnnouncementCharactersLimitWithFiles()
      : CHARACTER_LIMIT;
  };

  get isUrgentMessage() {
    return isUrgent(this.props.messageType);
  }

  uploadFile = (fileData: File) => {
    this.setState({ fileData });

    uploadFileRequest(fileData, false, (percent: number) => {
      if (this.state.fileData) {
        this.setState({ uploadProgress: percent });
      }
    })
      .then(response => {
        if (this.state.fileData) {
          this.setState({ file: response });
        }
      })
      .catch(err => this.setState({ uploadError: err }));
  };

  resetFileState = () => {
    this.setState({
      file: undefined,
      fileData: undefined,
      thumbnail: undefined,
      uploadError: undefined,
      uploadProgress: undefined,
    });
  };

  renderSenderInfoText = (district: ?ComposerOrganization, uiEventContext: UIEventContext) => {
    if (district) {
      const districtUuid = district.uuid;
      return (
        <Row size="xsmall" data-test="district-sender-info-text">
          <MediaObject figure="right">
            <div />
            <LayerTrigger
              layer={
                <Tooltip>Recipients aren’t able to reply to messages sent from a district</Tooltip>
              }
              placement="top"
              alignment="middle"
              onShow={() =>
                EventTracking.trackKey('announcements.composer.sending_as.click', {
                  ...uiEventContext,
                  organization_uuid: districtUuid,
                })
              }>
              <span>
                <TextWithGlyph
                  color="graphite"
                  size="xxsmall"
                  glyph="help"
                  glyphColor="graphite"
                  glyphSize="small">
                  Sending as {district.name}
                </TextWithGlyph>
              </span>
            </LayerTrigger>
          </MediaObject>
        </Row>
      );
    }
    return null;
  };

  renderModal() {
    return (
      <Modal isOpen={this.state.discardMessageDialogIsOpen} data-test="discard-message-dialog">
        <ConfirmDialog
          hideCloseButton
          title="Discard message?"
          onConfirm={this.forwardUserToNextLocation}
          onCancel={this.hideUnsavedMessageDialog}
          actionButtonText="Discard"
          cancelButtonText="Cancel"
        />
      </Modal>
    );
  }

  render() {
    const {
      targets,
      connectDropTarget,
      initialTargets,
      draftAnnouncement,
      districtIsSelected,
      district,
      messageType,
    } = this.props;

    const {
      text,
      scheduledTime,
      uuidForNewMessage,
      linkPreviews,
      file,
      fileData,
      thumbnail,
      uploadProgress,
      uploadError,
    } = this.state;
    const autoFocusComposer = initialTargets != null;

    return connectDropTarget(
      <div className="FullPageComposerView">
        {this.renderModal()}
        <SplitComposerHeader messageType={messageType} />
        {this.renderDropzoneModal()}
        {districtIsSelected && this.renderSenderInfoText(district, getUiEventContext(messageType))}
        <ComposerDataContainer initialTargets={initialTargets}>
          {dataProps => <ComposeMessageRecipientsInput {...dataProps} messageType={messageType} />}
        </ComposerDataContainer>
        <div className="FullPageComposerView-stream">
          <ChatWelcomeMessage hideWelcome />
        </div>
        <VisualComposer
          autoFocus={autoFocusComposer}
          draftAnnouncement={draftAnnouncement}
          className="ComposeMessage-composer-container"
          onMessageSent={this.handleComposerSent}
          charLimit={this.characterLimit()}
          charLimitWithFiles={this.characterLimitWithFiles()}
          districtIsSelected={districtIsSelected}
          targets={targets}
          onTextChange={this.changeText}
          uuidForNewMessage={uuidForNewMessage}
          onScheduledTimeChange={this.changeScheduledTime}
          scheduledTime={scheduledTime}
          text={text}
          linkPreviews={linkPreviews.filter(v => !isEmpty(v))}
          onRemoveLinkPreview={this.removeLinkPreview}
          orgUuid={district ? district.uuid : null}
          orgName={district ? district.name : null}
          isUrgentMessage={this.isUrgentMessage}
          addLinkedContent={this.addLinkedContent}
          uploadRequest={(fileContextKey: string, fileKey: string, data: File) =>
            this.uploadFile(data)
          }
          loadThumbnail={(fileContextKey: string, filename: string, newThumbnail: string) => {
            this.setState({ thumbnail: newThumbnail });
          }}
          fileUuid={file?.id}
          filePreview={
            (!!file || !!fileData) && (
              <ComposerFilePreview
                progress={uploadProgress}
                thumbnail={thumbnail}
                type={file?.content_type}
                error={!!uploadError}
                onDelete={() => {
                  this.resetFileState();
                }}
                onRetry={() => {
                  if (fileData) {
                    this.uploadFile(fileData);
                  }
                }}
              />
            )
          }
        />
      </div>
    );
  }
}

const dndEvents = {
  drop: (props: Props, monitor: DropTargetMonitor, component: FullPageComposerView) => {
    uploadFileHelper(
      '',
      monitor.getItem().files[0],
      (fileContextKey: string, fileKey: string, data: File) => component.uploadFile(data),
      (fileContextKey: string, filename: string, newThumbnail: string) =>
        component.setState({ thumbnail: newThumbnail })
    );

    EventTracking.trackKey(
      'announcements.announcement_modal.universal_composer.create_attachment.drag_and_drop',
      getUiEventContext(props.messageType)
    );
  },
};

const collect = (connector, monitor) => ({
  connectDropTarget: connector.dropTarget(),
  isDraggingFile: monitor.isOver(),
});

const withDropTarget = DropTarget(NativeTypes.FILE, dndEvents, collect);

const mapDispatchToProps = (dispatch: Function) =>
  bindActionCreators(
    {
      onFlush: flush,
      saveMessage,
      showToast,
      saveDidSearchRecipients,
    },
    dispatch
  );

const mapStateToProps = (state: Object) => {
  const { roleBasedMessagingSearch } = state;
  return {
    didSearchRecipients: roleBasedMessagingSearch.didSearchRecipients,
    initialTargets: roleBasedMessagingSearch.envelope.initialTargets,
    draftAnnouncement: roleBasedMessagingSearch.envelope.draftAnnouncement,
    onMessageSent: roleBasedMessagingSearch.envelope.onMessageSent,
    messageType: roleBasedMessagingSearch.envelope.messageType,
    targets: roleBasedMessagingSearch.targets,
    savedMessage: roleBasedMessagingSearch.message,
    district: roleBasedMessagingSearch.districtIsSelected
      ? roleBasedMessagingSearch.selection[0].organization
      : null,
    districtIsSelected: roleBasedMessagingSearch.districtIsSelected,
  };
};

const decorate = flow([withDropTarget, connect(mapStateToProps, mapDispatchToProps), withRouter]);
export default decorate(FullPageComposerView);
